/**
 * Text roles — the ONE type scale shared by web and mobile (design system,
 * 2026-10-24). Every piece of text on a record screen takes one role, named by
 * its job: the record's name is a `pageTitle`, a fact's name is a `label`, the
 * fact itself is a `value`. A label is never a value.
 *
 *   pageTitle 26/32 700 → sectionTitle 19/24 700 → cardTitle 15/20 600
 *   → value 14/20 500 → body 13/18 400 → meta 12/17 400 (muted)
 *   label 12/16 600 UPPERCASE .06em (muted) · figure 26/30 700 tabular
 *   button 13/18 600 · pill 12/16 600 · count 12/17 600 (muted, tabular)
 *
 *   Stepped down about 7% (2026-10-29, "quite large"): nothing at or under
 *   12 changed (the readable floor), tap targets stay 44. See ./typeScale.
 *
 * How each platform reads them:
 *   - Web: `textRole('label')` (./textRoleStyles) gives React CSSProperties
 *     with the role's color as a token CSS variable.
 *   - Mobile: `nativeTextRole('label', t.text)` gives a React Native text
 *     style (letter spacing in points, weight as a string).
 *
 * This file has NO imports so React Native can deep-import it
 * (`@turn-connect/ui/src/theme/textRoles`) without pulling in web-only code.
 * Reference: ./README.md, "Text roles".
 */

/** Which `text.*` color token a role uses by default. */
export type TextRoleColor = 'primary' | 'secondary' | 'muted';

export interface TextRole {
  /** px (web) / points (mobile). */
  fontSize: number;
  /** px (web) / points (mobile). */
  lineHeight: number;
  fontWeight: 400 | 500 | 600 | 700;
  /** In em; multiply by fontSize for points on mobile. */
  letterSpacingEm?: number;
  uppercase?: boolean;
  /** Figures line up (tiles, counts, tables). */
  tabularNums?: boolean;
  color: TextRoleColor;
}

export type TextRoleName =
  | 'pageTitle'
  | 'sectionTitle'
  | 'cardTitle'
  | 'label'
  | 'value'
  | 'figure'
  | 'meta'
  | 'body'
  | 'button'
  | 'pill'
  | 'count'
  | 'countBadge'
  | 'tab';

export const textRoles: Record<TextRoleName, TextRole> = {
  /** One per screen: the record's name (customer, work order). */
  pageTitle: { fontSize: 26, lineHeight: 32, fontWeight: 700, letterSpacingEm: -0.01, color: 'primary' },
  /** Heads a section panel (Vehicles, Jobs); its count beside it in `count`. */
  sectionTitle: { fontSize: 19, lineHeight: 24, fontWeight: 700, color: 'primary' },
  /** First line of a list row or card: job number, vehicle. */
  cardTitle: { fontSize: 15, lineHeight: 20, fontWeight: 600, color: 'primary' },
  /** A fact's or a figure's name. Always above or left of its value. */
  label: { fontSize: 12, lineHeight: 16, fontWeight: 600, letterSpacingEm: 0.06, uppercase: true, color: 'muted' },
  /** The answer to a label: phone, payer, a date. */
  value: { fontSize: 14, lineHeight: 20, fontWeight: 500, color: 'primary' },
  /** Big numbers in stat tiles. */
  figure: { fontSize: 26, lineHeight: 30, fontWeight: 700, tabularNums: true, color: 'primary' },
  /** Secondary facts on one line, separated by a middle dot. */
  meta: { fontSize: 12, lineHeight: 17, fontWeight: 400, color: 'muted' },
  /** Running copy and descriptions. */
  body: { fontSize: 13, lineHeight: 18, fontWeight: 400, color: 'secondary' },
  /** Button and tab labels. */
  button: { fontSize: 13, lineHeight: 18, fontWeight: 600, color: 'primary' },
  /** Status pill text, sentence case. */
  pill: { fontSize: 12, lineHeight: 16, fontWeight: 600, color: 'secondary' },
  /** A section's count beside its title. */
  count: { fontSize: 12, lineHeight: 17, fontWeight: 600, tabularNums: true, color: 'muted' },
  /** The count badge at the right of a section's header band. */
  countBadge: { fontSize: 12, lineHeight: 17, fontWeight: 700, tabularNums: true, color: 'primary' },
  /** A segmented tab's label (700 when chosen). */
  tab: { fontSize: 14, lineHeight: 19, fontWeight: 600, color: 'secondary' },
};

/**
 * The section box and its segmented tabs, in px/points (design system,
 * Alex 2026-10-24: "headers clearly segregated from values; tabs more
 * pronounced"). Colors: box `surface` + 1px `border`; header `band`
 * with a 2px `borderStrong` rule; count badge `surfaceRaised` + `border`;
 * tab track `bgSubtle` + `border`; chosen tab `brandFill` / `onAccent`, its
 * count badge `onAccentSubtle`, other count badges `washStrong`; rows inside
 * the box on the page ground `bg`.
 */
export const sectionSpec = {
  radius: 8,
  band: { paddingVertical: 10, paddingHorizontal: 14, ruleWidth: 2 },
  body: { padding: 14, gap: 10 },
  countBadge: { minWidth: 26, height: 22, paddingHorizontal: 8 },
  tabs: { trackRadius: 8, trackPadding: 4, gap: 4, tabRadius: 6, minHeight: 44, countMinWidth: 22, countHeight: 20, countFontSize: 12 },
  /** A list row; `toneShare` is how much of `warning`/`danger` a toned row mixes into the page ground. */
  row: { radius: 8, paddingVertical: 10, paddingHorizontal: 14, gap: 8, toneShare: 0.15 },
} as const;

/**
 * How tightly a screen is laid out (design system, Alex 2026-10-24: "Report
 * font is really big … perhaps it can be made a bit more compact").
 *   - `default`: record and work screens (Customers, My Work, Help): 44px
 *     controls, 20px section titles, 15px tabs.
 *   - `compact`: data-heavy screens (Reports, big tables): the numbers below.
 * Phones keep 44px touch targets in both: compact controls grow back to
 * `touchControlHeight` at `touchMaxWidth` and narrower (web: the
 * `--tc-compact-*` variables in ./cssVars). Label/value roles and the solid
 * `brandFill` chosen tab are the same in both densities.
 */
export type Density = 'default' | 'compact';

/** Compact density, in px/points. Anything not listed keeps `sectionSpec`. */
export const compactSpec = {
  /** Segments, buttons, inputs and selects on a desktop or iPad. */
  controlHeight: 36,
  /** The same controls on a phone (touch target). */
  touchControlHeight: 44,
  /** Widest viewport that counts as a phone (matches the web `webIsMobile` < 768). */
  touchMaxWidth: 767,
  /** Space between control groups and blocks. */
  gap: 12,
  /** Segmented tabs: sized to their labels (not full width) on a desktop. */
  tabs: {
    fontSize: 13,
    lineHeight: 17,
    paddingHorizontal: 12,
    trackPadding: 3,
    gap: 2,
    countMinWidth: 20,
    countHeight: 18,
    countFontSize: 11,
  },
  button: { paddingHorizontal: 12 },
  band: { paddingVertical: 8, paddingHorizontal: 12, gap: 10, titleFontSize: 17, titleLineHeight: 22, collapsibleMinHeight: 48 },
  body: { padding: 10, gap: 10 },
  /** Table cells: header keeps the `label` role, just tighter. */
  table: { cellPaddingVertical: 6, cellPaddingHorizontal: 9 },
  /** Stat tile figures (record screens use the 28px `figure`). */
  figure: { fontSize: 19, lineHeight: 24 },
  tile: { paddingVertical: 7, paddingHorizontal: 11 },
} as const;

/** Text shown for a fact with no value: an en dash (in `meta`), never an empty row. */
export const EMPTY_VALUE = '–';

/** The `text.*` colors a native style needs (pass `useAppTokens().text`). */
export interface TextRoleColors {
  primary: string;
  secondary: string;
  muted: string;
}

export interface NativeTextRoleStyle {
  fontSize: number;
  lineHeight: number;
  fontWeight: '400' | '500' | '600' | '700';
  color: string;
  letterSpacing?: number;
  textTransform?: 'uppercase';
  fontVariant?: 'tabular-nums'[];
}

/** A React Native text style for a role, colored from the active theme's `text.*` tokens. */
export function nativeTextRole(
  name: TextRoleName,
  colors: TextRoleColors,
  color: TextRoleColor = textRoles[name].color
): NativeTextRoleStyle {
  const r = textRoles[name];
  const style: NativeTextRoleStyle = {
    fontSize: r.fontSize,
    lineHeight: r.lineHeight,
    fontWeight: String(r.fontWeight) as NativeTextRoleStyle['fontWeight'],
    color: colors[color],
  };
  if (r.letterSpacingEm) style.letterSpacing = Math.round(r.letterSpacingEm * r.fontSize * 100) / 100;
  if (r.uppercase) style.textTransform = 'uppercase';
  if (r.tabularNums) style.fontVariant = ['tabular-nums'];
  return style;
}

/** A stat tile's label is long when it would not fit a third of a phone row ("Finished, not invoiced"). */
export const LONG_STAT_LABEL = 14;

/**
 * How many columns each stat tile spans in an even grid: long labels span 2,
 * and the last tile stretches so no row is left part-empty (never a lone tile
 * beside a gap). On wide screens (columns >= tile count) every tile spans 1.
 */
export function statTileSpans(labels: readonly string[], columns: number): number[] {
  if (columns >= labels.length) return labels.map(() => 1);
  const spans = labels.map((l) => (l.length > LONG_STAT_LABEL ? Math.min(2, columns) : 1));
  // Lay rows out in order; a tile that does not fit starts a new row and the
  // row before it stretches its last tile to fill.
  let used = 0;
  for (let i = 0; i < spans.length; i += 1) {
    if (used + spans[i] > columns) {
      spans[i - 1] += columns - used;
      used = 0;
    }
    used += spans[i];
    if (used === columns) used = 0;
  }
  if (used > 0) spans[spans.length - 1] += columns - used;
  return spans;
}
